"use client";

import { c as _c } from "react/compiler-runtime";
import { ReactNode, useState, useEffect, useCallback } from "react";
import { useTranslations } from "next-intl";
import { useSession } from "next-auth/react";
import { useIdleTimer, IIdleTimer } from "react-idle-timer";
import { useRouter } from "@/i18n/navigation";
import { Heading2, Button } from "@ops-gsic/cxp-component-library-react";
import AppModal from "@/components/modal";
import { ModalBody, LeadText, ModalFooter } from "@/components/modal/styles";
import { formatRemainingSessionTime } from "@/utils/formatters/formatRemainingSessionTime";
import { Session } from "@/types/session";
interface WithTimeoutProps {
  requireAuthentication?: boolean;
  children: ReactNode;
}
const END_SESSION_MESSAGE = "END_SESSION";
export function WithTimeout(t0) {
  const $ = _c(43);
  const {
    requireAuthentication: t1,
    children
  } = t0;
  const requireAuthentication = t1 === undefined ? true : t1;
  const router = useRouter();
  const t = useTranslations("Common.components.sessionTimeoutModal");
  const tCommon = useTranslations("Common");
  let t2;
  if ($[0] !== requireAuthentication || $[1] !== router) {
    t2 = {
      required: requireAuthentication,
      onUnauthenticated() {
        if (requireAuthentication) {
          router.replace("/logout");
        }
      }
    };
    $[0] = requireAuthentication;
    $[1] = router;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: session,
    status,
    update
  } = useSession(t2);
  const [showPrompt, setShowPrompt] = useState(false);
  const [remaining, setRemaining] = useState(0);
  let t3;
  if ($[3] !== router || $[4] !== status || $[5] !== tCommon) {
    t3 = idleTimer => () => {
      sessionStorage.clear();
      if (idleTimer) {
        idleTimer.message(END_SESSION_MESSAGE, false);
      }
      if (status === "authenticated") {
        router.replace("/logout");
      } else {
        router.replace(tCommon("serviceOntarioSP") ?? "#");
      }
    };
    $[3] = router;
    $[4] = status;
    $[5] = tCommon;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const cancelSession = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setShowPrompt(false);
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onActive = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShowPrompt(true);
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const onPrompt = t5;
  let t6;
  if ($[9] !== cancelSession) {
    t6 = (data, idleTimer_0) => {
      if (data === END_SESSION_MESSAGE && idleTimer_0) {
        cancelSession(idleTimer_0)();
      }
    };
    $[9] = cancelSession;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const onMessage = t6;
  const sessionDuration = Number(process.env.NEXTAUTH_SESSION_DURATION ?? 1200) * 1000;
  const promptDuration = Number(process.env.SESSION_TIMEOUT_MODAL_DURATION ?? 120) * 1000;
  let t7;
  if ($[11] !== onMessage) {
    t7 = {
      timeout: sessionDuration,
      promptBeforeIdle: promptDuration,
      throttle: 500,
      crossTab: true,
      syncTimers: 100,
      onActive,
      onPrompt,
      onMessage
    };
    $[11] = onMessage;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const idleTimer_1 = useIdleTimer(t7);
  const {
    getRemainingTime,
    activate
  } = idleTimer_1;
  let t8;
  if ($[13] !== activate || $[14] !== cancelSession || $[15] !== idleTimer_1 || $[16] !== status || $[17] !== update) {
    t8 = async () => {
      if (status === "authenticated") {
        try {
          await update();
        } catch {
          cancelSession(idleTimer_1)();
          return;
        }
      }
      activate();
      setShowPrompt(false);
    };
    $[13] = activate;
    $[14] = cancelSession;
    $[15] = idleTimer_1;
    $[16] = status;
    $[17] = update;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const continueSession = t8;
  let t10;
  let t9;
  if ($[19] !== cancelSession || $[20] !== idleTimer_1 || $[21] !== session) {
    t9 = () => {
      if ((session as Session)?.error === "RefreshAccessTokenError") {
        cancelSession(idleTimer_1)();
      }
    };
    t10 = [session, cancelSession, idleTimer_1];
    $[19] = cancelSession;
    $[20] = idleTimer_1;
    $[21] = session;
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[24] !== cancelSession || $[25] !== getRemainingTime || $[26] !== idleTimer_1) {
    t11 = () => {
      const interval = setInterval(() => {
        const remainingSeconds = Math.ceil(getRemainingTime() / 1000);
        if (remainingSeconds > 0) {
          setRemaining(remainingSeconds);
        } else {
          setShowPrompt(false);
          cancelSession(idleTimer_1)();
        }
      }, 500);
      return () => clearInterval(interval);
    };
    t12 = [getRemainingTime, cancelSession, idleTimer_1];
    $[24] = cancelSession;
    $[25] = getRemainingTime;
    $[26] = idleTimer_1;
    $[27] = t11;
    $[28] = t12;
  } else {
    t11 = $[27];
    t12 = $[28];
  }
  useEffect(t11, t12);
  let t13;
  if ($[29] !== activate) {
    t13 = () => {
      activate();
    };
    $[29] = activate;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  useEffect(t13, t14);
  let t15;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      content: {
        border: "3px solid black",
        maxWidth: "500px",
        padding: "1.5rem"
      }
    };
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const modalStyles = t15;
  if (status === "loading") {
    return null;
  }
  let t16;
  if ($[33] !== cancelSession || $[34] !== continueSession || $[35] !== idleTimer_1 || $[36] !== remaining || $[37] !== showPrompt || $[38] !== t) {
    t16 = showPrompt && <AppModal modalIsOpen={showPrompt} shouldCloseOnEsc={false} shouldCloseOnOverlayClick={false} customStyles={modalStyles}><ModalBody><Heading2>{t("header")}</Heading2><LeadText>{t("forYour")} {formatRemainingSessionTime(remaining, t)}.{" "}{t("weWill")}</LeadText><LeadText>{t("clickContinue")}</LeadText></ModalBody><ModalFooter><Button type="button" onClick={continueSession}>{t("continue")}</Button><Button type="button" appearance="tertiary" onClick={cancelSession(idleTimer_1)}>{t("cancel")}</Button></ModalFooter></AppModal>;
    $[33] = cancelSession;
    $[34] = continueSession;
    $[35] = idleTimer_1;
    $[36] = remaining;
    $[37] = showPrompt;
    $[38] = t;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== children || $[41] !== t16) {
    t17 = <>{children}{t16}</>;
    $[40] = children;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  return t17;
}